<template>
  <v-container fluid class="topping-form-page">
    <!-- PAGE HEADER -->
    <div class="ap-header-row mb-5 d-flex align-center justify-space-between flex-wrap" style="gap: 16px;">
      <div class="d-flex align-center" style="gap: 12px;">
        <v-btn
          depressed
          outlined
          class="ap-back-btn rounded-lg"
          @click="goBack"
        >
          <v-icon small color="primary">fal fa-arrow-left</v-icon>
        </v-btn>
        <div>
          <div class="d-flex align-center" style="gap: 8px;">
            <h1 class="topping-form-title font-weight-black mb-0">
              {{ isEdit ? $t("update") : $t("new_data") }} {{ $t("topping") }}
            </h1>
            <v-chip
              small
              label
              :color="form.is_active ? 'success' : 'grey'"
              class="font-weight-bold white--text"
            >
              {{ form.is_active ? $t("active") : $t("inactive") }}
            </v-chip>
          </div>
          <div class="text-caption grey--text mt-1">
            {{ isEdit ? $t("edit_topping_subtitle") || "Update topping names, prices, and recipe ingredients" : $t("create_topping_subtitle") || "Configure topping details, multilingual names, and inventory recipe" }}
          </div>
        </div>
      </div>

      <!-- Quick top actions on desktop -->
      <div class="d-none d-sm-flex align-center" style="gap: 10px;">
        <v-btn
          depressed
          outlined
          class="text-none rounded-lg font-weight-bold px-4"
          @click="goBack"
        >
          {{ $t("cancel") }}
        </v-btn>
        <v-btn
          depressed
          color="primary"
          class="text-none rounded-lg font-weight-bold px-5 white--text"
          :loading="saving"
          :disabled="saving"
          @click="submitForm"
        >
          <v-icon left small>{{ isEdit ? "fal fa-save" : "fal fa-check" }}</v-icon>
          {{ isEdit ? $t("update") : $t("save") }}
        </v-btn>
      </div>
    </div>

    <!-- MAIN FORM GRID (8 COLS MAIN + 4 COLS SIDEBAR) -->
    <v-row v-if="!loadingPage">
      <!-- ============ MAIN COLUMN (8 COLS) ============ -->
      <v-col cols="12" lg="8" class="pr-lg-3">
        <!-- 1. GENERAL INFORMATION & MULTILINGUAL NAMES -->
        <v-card class="ap-card mb-4" outlined elevation="0">
          <v-card-title class="py-3 px-4 d-flex align-center justify-space-between border-bottom ap-card-title">
            <div class="d-flex align-center">
              <span class="ap-card-accent mr-2"></span>
              <span class="font-weight-bold text-subtitle-2">{{ $t("general_information") || "General Information" }}</span>
            </div>
            <!-- Language Pills Toggle -->
            <div class="d-flex align-center" style="gap: 6px;">
              <span class="text-caption grey--text mr-1">{{ $t("ui_add_language") }}:</span>
              <v-chip
                x-small
                outlined
                :color="showEn ? 'primary' : ''"
                class="font-weight-bold"
                @click="showEn = !showEn"
              >
                <LanguageFlag code="en" class="mr-1" /> EN
              </v-chip>
              <v-chip
                x-small
                outlined
                :color="showTh ? 'primary' : ''"
                class="font-weight-bold"
                @click="showTh = !showTh"
              >
                <LanguageFlag code="th" class="mr-1" /> TH
              </v-chip>
              <v-chip
                x-small
                outlined
                :color="showCn ? 'primary' : ''"
                class="font-weight-bold"
                @click="showCn = !showCn"
              >
                <LanguageFlag code="ch" class="mr-1" /> CN
              </v-chip>
            </div>
          </v-card-title>
          <v-card-text class="pa-4 pa-md-5">
            <!-- Lao Name (Primary) -->
            <div class="mb-3">
              <div class="section-label">
                {{ $t("topping_name") }} ({{ $t("la") }}) <span class="required-mark">*</span>
              </div>
              <TTextField
                class="language-name-field"
                skip-translation
                flag-code="la"
                :placeholder="$t('enter_topping_name') || 'ໃສ່ຊື່ທັອບປິ້ງ'"
                v-model="form.name_la"
                required
                :counter="true"
                :maxlength="200"
                :errorMessages="nameLaErrors"
              />
            </div>

            <!-- Optional Multilingual Names -->
            <div v-if="showEn || showTh || showCn" class="form-grid mb-2">
              <div v-if="showEn">
                <div class="section-label">{{ $t("name_en") }}</div>
                <TTextField
                  skip-translation
                  class="language-name-field"
                  flag-code="en"
                  :placeholder="$t('name_en')"
                  v-model="form.name_en"
                  :counter="true"
                  :maxlength="200"
                />
              </div>

              <div v-if="showTh">
                <div class="section-label">{{ $t("name_th") }}</div>
                <TTextField
                  skip-translation
                  class="language-name-field"
                  flag-code="th"
                  :placeholder="$t('name_th')"
                  v-model="form.name_th"
                  :counter="true"
                  :maxlength="200"
                />
              </div>

              <div v-if="showCn">
                <div class="section-label">{{ $t("name_cn") }}</div>
                <TTextField
                  skip-translation
                  class="language-name-field"
                  flag-code="ch"
                  :placeholder="$t('name_cn')"
                  v-model="form.name_cn"
                  :counter="true"
                  :maxlength="200"
                />
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- 2. PRICING & CURRENCY -->
        <v-card class="ap-card mb-4" outlined elevation="0">
          <v-card-title class="py-3 px-4 d-flex align-center border-bottom ap-card-title">
            <span class="ap-card-accent mr-2"></span>
            <span class="font-weight-bold text-subtitle-2">{{ $t("price") }} & {{ $t("currency") }}</span>
          </v-card-title>
          <v-card-text class="pa-4 pa-md-5">
            <div class="mb-4">
              <div class="section-label">{{ $t("currency") }}</div>
              <v-chip-group v-model="form.currency_id" mandatory column>
                <v-chip
                  :value="null"
                  outlined
                  filter
                  :color="!form.currency_id ? 'primary' : ''"
                  class="font-weight-bold"
                >
                  {{ $t("store_default_currency") }}
                </v-chip>
                <v-chip
                  v-for="currency in currencies"
                  :key="currency.id"
                  :value="currency.id"
                  outlined
                  filter
                  :color="form.currency_id === currency.id ? 'primary' : ''"
                  class="font-weight-bold"
                >
                  <v-avatar v-if="currency.flag" size="16" class="mr-1">
                    <v-img :src="currency.flag" />
                  </v-avatar>
                  {{ currency.code }}
                </v-chip>
              </v-chip-group>
            </div>

            <div>
              <div class="section-label">
                {{ $t("topping_price") }} <span class="required-mark">*</span>
              </div>
              <CurrencyInput
                label=""
                skipTranslation
                v-model="form.price"
                :errorMessages="priceErrors"
                @input="$v.form.price.$touch()"
                @blur="$v.form.price.$touch()"
              />
            </div>
          </v-card-text>
        </v-card>

        <!-- 3. INVENTORY & LINKED INGREDIENTS -->
        <v-card class="ap-card mb-4" outlined elevation="0">
          <v-card-title class="py-3 px-4 d-flex align-center justify-space-between border-bottom ap-card-title">
            <div class="d-flex align-center">
              <span class="ap-card-accent mr-2"></span>
              <span class="font-weight-bold text-subtitle-2">{{ $t("link_to_ingredient") }}</span>
            </div>
            <v-switch
              v-model="linkIngredient"
              inset
              color="primary"
              hide-details
              class="enable-switch-control ma-0 pa-0"
            />
          </v-card-title>
          <v-card-text class="pa-4 pa-md-5">
            <template v-if="linkIngredient">
              <div class="participant-box mb-4">
                <div class="section-label mb-2">{{ $t("add_ingredient") || "Add Recipe Ingredient" }}</div>
                <div class="mb-3">
                  <TextSelect
                    :key="ingredientSelectKey"
                    searchable
                    :cache-items="false"
                    :placeholder="false"
                    label="select_ingredient"
                    itemValue="id"
                    text="name_la"
                    :returnObject="false"
                    :items="ingredientOptions"
                    v-model="draft.ingredient_id"
                  />
                </div>

                <div class="form-grid mb-3">
                  <div>
                    <div class="section-label">{{ $t("usage_quantity") }}</div>
                    <CurrencyInput
                      label=""
                      skipTranslation
                      v-model="draft.quantity"
                    />
                  </div>
                  <div>
                    <div class="section-label">{{ $t("select_unit") }}</div>
                    <TextSelect
                      searchable
                      :placeholder="false"
                      label="select_unit"
                      itemValue="id"
                      text="label"
                      :returnObject="false"
                      :items="draftUnitOptions"
                      v-model="draft.unit_id"
                      :disabled="!draft.ingredient_id"
                    />
                  </div>
                </div>

                <v-btn
                  depressed
                  outlined
                  color="primary"
                  block
                  class="rounded-lg text-none font-weight-bold"
                  :disabled="!canAddLine"
                  @click="addLine"
                >
                  <v-icon left small>fal fa-plus</v-icon>
                  {{ $t("add") }}
                </v-btn>

                <div v-if="ingredientErrors.length" class="line-error mt-2">
                  {{ ingredientErrors[0] }}
                </div>
              </div>

              <!-- Ingredient Line Items List -->
              <div v-if="form.ingredients.length" class="ingredient-line-list mb-4">
                <div class="section-label">{{ $t("ingredient_list") || "Recipe Ingredients" }} ({{ form.ingredients.length }})</div>
                <div
                  v-for="(line, index) in form.ingredients"
                  :key="`${line.ingredient_id}-${index}`"
                  class="ingredient-line-row"
                >
                  <div class="ingredient-line-info">
                    <div class="ingredient-line-name font-weight-bold">{{ ingredientName(line) }}</div>
                    <div class="ingredient-line-meta">
                      {{ numberText(line.quantity) }} {{ line.unit_name }} ·
                      {{ numberText(line.cost) }} ₭
                    </div>
                  </div>
                  <v-btn icon small @click="removeLine(index)">
                    <v-icon small color="error">fal fa-trash-alt</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Cost Preview Bar -->
              <div class="cost-preview">
                <div class="d-flex align-center">
                  <v-icon small color="primary" class="mr-2">fal fa-calculator</v-icon>
                  <span class="cost-preview-label font-weight-bold">{{ $t("topping_cost") }}</span>
                </div>
                <span class="cost-preview-value font-weight-black">{{ numberText(computedCost) }} {{ currentCurrencyCode }}</span>
              </div>
            </template>

            <div v-else class="no-ingredient-hint pa-4 text-center rounded-lg bg-slate-50">
              <v-icon large color="#94a3b8" class="mb-2">fal fa-cookie-bite</v-icon>
              <div class="text-body-2 text-secondary font-weight-medium">{{ $t("no_ingredient_linked") }}</div>
              <div class="text-caption grey--text mt-1">Turn on the switch above if this topping deducts inventory ingredients when ordered.</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- ============ SIDEBAR COLUMN (4 COLS) ============ -->
      <v-col cols="12" lg="4" class="pl-lg-3">
        <!-- 1. STATUS & LIVE PREVIEW -->
        <v-card class="ap-card mb-4" outlined elevation="0">
          <v-card-title class="py-3 px-4 d-flex align-center justify-space-between border-bottom ap-card-title">
            <div class="d-flex align-center">
              <span class="ap-card-accent mr-2"></span>
              <span class="font-weight-bold text-subtitle-2">{{ $t("status_and_preview") || "Status & Preview" }}</span>
            </div>
            <v-chip x-small label :color="form.is_active ? 'success' : 'grey'" class="font-weight-bold white--text">
              {{ form.is_active ? $t("active") : $t("inactive") }}
            </v-chip>
          </v-card-title>
          <v-card-text class="pa-4">
            <!-- Active switch row -->
            <div class="enable-switch-row mb-4 pa-3 rounded-lg bg-slate-50">
              <div>
                <div class="font-weight-bold text-body-2">{{ $t("active") }}</div>
                <div class="text-caption grey--text">{{ form.is_active ? $t("topping_active_hint") || "Available on POS cart" : $t("topping_inactive_hint") || "Hidden on POS" }}</div>
              </div>
              <v-switch
                v-model="form.is_active"
                inset
                color="primary"
                hide-details
                class="enable-switch-control ma-0 pa-0"
              />
            </div>

            <!-- LIVE TOPPING PREVIEW CARD -->
            <div class="ps-topping-preview-card" :class="{ 'ps-topping-preview-card--inactive': !form.is_active }">
              <div class="ps-topping-preview-top d-flex align-center justify-space-between mb-3">
                <div class="d-flex align-center">
                  <v-icon x-small color="primary" class="mr-1">fal fa-cookie-bite</v-icon>
                  <span class="ps-topping-preview-brand">TOPPING PREVIEW</span>
                </div>
                <v-chip x-small label :color="form.is_active ? 'success' : 'grey'" class="font-weight-bold white--text">
                  {{ form.is_active ? $t("active") : $t("inactive") }}
                </v-chip>
              </div>

              <!-- Media / Avatar -->
              <div class="d-flex align-center mb-3" style="gap: 12px;">
                <div class="ps-topping-preview-avatar">
                  <v-img v-if="previewImageSrc" :src="previewImageSrc" contain height="48" width="48" class="rounded-lg" />
                  <v-icon v-else size="24" color="primary">fal fa-cookie-bite</v-icon>
                </div>
                <div class="flex-grow-1 overflow-hidden">
                  <div class="ps-topping-preview-title text-truncate font-weight-bold">
                    {{ form.name_la || form.name_en || $t("topping_name") }}
                  </div>
                  <div class="ps-topping-preview-sub text-truncate text-caption grey--text">
                    {{ previewSecondaryName || "Multilingual name" }}
                  </div>
                </div>
              </div>

              <div class="ps-topping-preview-divider my-2"></div>

              <!-- Price & Cost breakdown -->
              <div class="d-flex align-center justify-space-between py-1">
                <span class="text-caption grey--text">{{ $t("topping_price") }}</span>
                <span class="font-weight-black text-body-1 primary--text">
                  {{ numberText(form.price) }} {{ currentCurrencyCode }}
                </span>
              </div>

              <div v-if="linkIngredient && form.ingredients.length" class="d-flex align-center justify-space-between py-1">
                <span class="text-caption grey--text">{{ $t("topping_cost") }}</span>
                <span class="text-caption font-weight-bold grey--text">
                  {{ numberText(computedCost) }} {{ currentCurrencyCode }}
                </span>
              </div>

              <div v-if="profitMarginText" class="d-flex align-center justify-space-between py-1">
                <span class="text-caption grey--text">{{ $t("gross_margin") || "Est. Margin" }}</span>
                <v-chip x-small label color="success" outlined class="font-weight-bold">
                  {{ profitMarginText }}
                </v-chip>
              </div>

              <div class="ps-topping-preview-footer mt-2 pt-2 border-top d-flex align-center justify-space-between text-caption grey--text">
                <span>{{ linkIngredient && form.ingredients.length ? `${form.ingredients.length} ${$t("ingredients")}` : $t("no_ingredient_linked") }}</span>
                <span v-if="form.is_active" class="success--text font-weight-medium">● POS Ready</span>
                <span v-else class="grey--text font-weight-medium">○ Hidden</span>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- 2. TOPPING IMAGE -->
        <v-card class="ap-card mb-4" outlined elevation="0">
          <v-card-title class="py-3 px-4 d-flex align-center border-bottom ap-card-title">
            <span class="ap-card-accent mr-2"></span>
            <span class="font-weight-bold text-subtitle-2">{{ $t("image") }}</span>
          </v-card-title>
          <v-card-text class="pa-4">
            <DragDropUpload
              v-model="imgFile"
              accept="image/png,image/jpeg,image/webp"
              :max-size="4 * 1024 * 1024"
              :max-size-m-b="4"
            />
            <div class="upload-hint mt-2 text-caption grey--text">{{ $t("image_upload_hint") || "Upload square PNG, JPG, or WebP up to 4MB" }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- SKELETON PLACEHOLDER -->
    <v-row v-else>
      <v-col cols="12" lg="8" class="pr-lg-3">
        <v-card outlined class="rounded-xl ap-card mb-4">
          <v-card-text class="pa-5">
            <div class="skeleton-block skeleton-label mb-3"></div>
            <div class="skeleton-block skeleton-field mb-4"></div>
            <div class="skeleton-block skeleton-label mb-3"></div>
            <div class="skeleton-block skeleton-field"></div>
          </v-card-text>
        </v-card>
        <v-card outlined class="rounded-xl ap-card mb-4">
          <v-card-text class="pa-5">
            <div class="skeleton-block skeleton-label mb-3"></div>
            <div class="skeleton-block skeleton-field"></div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" lg="4" class="pl-lg-3">
        <v-card outlined class="rounded-xl ap-card mb-4">
          <v-card-text class="pa-5">
            <div class="skeleton-block skeleton-label mb-3"></div>
            <div class="skeleton-block skeleton-upload-area mb-4"></div>
            <div class="skeleton-block skeleton-line"></div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- BOTTOM ACTION BAR -->
    <div v-if="!loadingPage" class="topping-form-actions-bar mt-5 pa-4 ap-card d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
      <v-btn
        depressed
        outlined
        class="text-none rounded-lg font-weight-bold px-4"
        @click="goBack"
      >
        {{ $t("cancel") }}
      </v-btn>
      <v-btn
        depressed
        color="primary"
        class="text-none rounded-lg font-weight-bold px-6 white--text form-action-btn-primary"
        :loading="saving"
        :disabled="saving"
        @click="submitForm"
      >
        <v-icon left small>{{ isEdit ? "fal fa-save" : "fal fa-check" }}</v-icon>
        {{ isEdit ? $t("update") : $t("save") }}
      </v-btn>
    </div>
  </v-container>
</template>
  </v-container>
</template>

<script>
/* global Help */
import TextSelect from "@/components/global/TextSelect";
import TTextField from "@/components/global/TextField";
import CurrencyInput from "@/components/CurrencyInput";
import DragDropUpload from "@/components/global/DragDropUpload.vue";
import LanguageFlag from "@/components/global/LanguageFlag.vue";
import { success } from "@/controllers/alert";
import { required } from "vuelidate/lib/validators";
import { connectedUnitIds, conversionFactor } from "@/utils/ingredientConversion";

export default {
  name: "ToppingFormPage",
  components: {
    TextSelect,
    TTextField,
    CurrencyInput,
    DragDropUpload,
    LanguageFlag,
  },
  data: () => ({
    loadingPage: true,
    saving: false,
    ingredients: [],
    currencies: [],
    showEn: false,
    showTh: false,
    showCn: false,
    linkIngredient: false,
    imgFile: null,
    submitted: false,
    // One in-progress row for the "add ingredient" form — separate from
    // form.ingredients (the committed list) so a half-filled row never gets
    // submitted by accident.
    draft: {
      ingredient_id: null,
      quantity: null,
      unit_id: null,
    },
    form: {
      id: null,
      name_la: "",
      name_en: "",
      name_th: "",
      name_cn: "",
      img: null,
      ingredients: [],
      price: 0,
      currency_id: null,
      is_active: true,
    },
  }),
  validations: {
    form: {
      name_la: { required },
      price: { required },
    },
  },
  computed: {
    toppingId() {
      return this.$route.params.id;
    },
    isEdit() {
      return Boolean(this.toppingId);
    },
    draftIngredient() {
      return this.ingredients.find((item) => item.id === this.draft.ingredient_id) || null;
    },
    // Ingredients already added to the list are removed from the picker —
    // adding the same ingredient twice would just silently overwrite one
    // line's cost with the other's on the backend (unique per topping+ingredient).
    ingredientOptions() {
      const usedIds = new Set(this.form.ingredients.map((line) => Number(line.ingredient_id)));
      return this.ingredients.filter((item) => !usedIds.has(Number(item.id)));
    },
    // Forces the autocomplete to remount whenever the added-lines set
    // changes. cache-items:false stops IT from re-adding items to its own
    // cache, but Vuetify's autocomplete still keeps whatever it already
    // rendered in its menu for the current mount — without this remount, a
    // just-added ingredient stayed clickable in the dropdown and could be
    // submitted twice (the exact duplicate_ingredient case this fixes).
    ingredientSelectKey() {
      return this.form.ingredients.map((line) => line.ingredient_id).sort((a, b) => a - b).join(",");
    },
    // Only units reachable through the ingredient's own "usage" conversions
    // are offered — the same restriction the BOM recipe editor applies. A
    // free pick from every unit in the store let the merchant choose one
    // with no conversion path to the ingredient's base unit, which made the
    // cost uncomputable (or silently wrong if computed as a flat multiply).
    draftUnitOptions() {
      return this.unitsForIngredient(this.draftIngredient);
    },
    canAddLine() {
      if (!this.draft.ingredient_id || !this.draft.unit_id || Number(this.draft.quantity) <= 0) {
        return false;
      }
      // Belt-and-suspenders: the dropdown already excludes ingredients that
      // are in form.ingredients (see ingredientOptions), but Vuetify
      // autocompletes can retain a stale cached option across a fast
      // re-render, which let the same ingredient reach the backend twice
      // and trip its duplicate_ingredient guard. Re-check here so Add is
      // always a hard no regardless of what the dropdown displayed.
      return !this.form.ingredients.some(
        (line) => Number(line.ingredient_id) === Number(this.draft.ingredient_id)
      );
    },
    computedCost() {
      return this.form.ingredients.reduce((sum, line) => sum + Number(line.cost || 0), 0);
    },
    nameLaErrors() {
      return this.requiredErrors("name_la");
    },
    priceErrors() {
      return this.requiredErrors("price");
    },
    ingredientErrors() {
      if (!this.submitted || !this.linkIngredient) return [];
      return this.form.ingredients.length ? [] : [this.$t("field_required")];
    },
    previewImageSrc() {
      if (this.imgFile instanceof File) {
        return URL.createObjectURL(this.imgFile);
      }
      if (typeof this.imgFile === "string" && this.imgFile) {
        return this.imgFile;
      }
      return null;
    },
    previewSecondaryName() {
      const parts = [this.form.name_en, this.form.name_th, this.form.name_cn].filter(Boolean);
      return parts.join(" · ") || "";
    },
    currentCurrencyCode() {
      if (!this.form.currency_id) return "₭";
      const cur = this.currencies.find((c) => c.id === this.form.currency_id);
      return cur ? cur.code : "₭";
    },
    profitMarginText() {
      const price = Number(this.form.price) || 0;
      const cost = Number(this.computedCost) || 0;
      if (!price || !this.linkIngredient || !this.form.ingredients.length) return null;
      const profit = price - cost;
      const marginPct = price > 0 ? Math.round((profit / price) * 100) : 0;
      return `${marginPct}%`;
    },
  },
  watch: {
    // Switching the draft's ingredient invalidates the old unit choice (a
    // unit valid for one ingredient's conversion graph may not connect to
    // another's base unit), so re-pick from the new ingredient's options.
    "draft.ingredient_id"() {
      this.draft.unit_id = this.draftUnitOptions[0]?.id || null;
    },
  },
  async created() {
    try {
      await Promise.all([this.fetchIngredients(), this.fetchCurrencies()]);
      if (this.isEdit) {
        await this.fetchTopping();
      }
    } finally {
      this.loadingPage = false;
    }
  },
  methods: {
    async fetchTopping() {
      const { data } = await this.$http.get(`toppings/${this.toppingId}`);
      if (data.status) {
        this.form = this.mapToppingToForm(data.data);
        this.linkIngredient = this.form.ingredients.length > 0;
        this.showEn = Boolean(this.form.name_en);
        this.showTh = Boolean(this.form.name_th);
        this.showCn = Boolean(this.form.name_cn);
        // DragDropUpload's v-model accepts a URL string to preview an
        // existing file, same as supplier logo editing — without this the
        // upload box showed empty on every edit even when the topping
        // already had an image, since imgFile only ever tracked new File
        // uploads.
        this.imgFile = this.form.img ? Help.img(this.form.img) : null;
      }
    },
    async fetchIngredients() {
      const { data } = await this.$http.get("ingredients?include_usage_conversions=1");
      if (data.status) {
        this.ingredients = Array.isArray(data.data) ? data.data : [];
      }
    },
    // Same source add-product.vue uses. Leaving currency_id null means the
    // topping just stays in the store's own base currency (getExchangeRate
    // on the backend already treats null as "no conversion needed") — the
    // chip list only matters for a topping priced in a different currency.
    async fetchCurrencies() {
      const { data } = await this.$http.get("get_currency");
      if (data.status) {
        this.currencies = (Array.isArray(data.data) ? data.data : []).filter(
          (currency) => currency.store_status === "active"
        );
      }
    },
    // Base unit plus every unit connected to it through an active "usage"
    // conversion — mirrors VariantRecipeEditor's unitsForIngredient so a
    // topping's usage line always resolves to a valid cost.
    unitsForIngredient(ingredient) {
      if (!ingredient || !ingredient.unit_id) return [];

      const baseUnitId = Number(ingredient.unit_id);
      const units = new Map();
      units.set(baseUnitId, {
        id: baseUnitId,
        label: this.unitLabel({ name: ingredient.unit_name, name_en: ingredient.unit_name_en }),
      });

      const usageConversions = (ingredient.unit_conversions || []).filter(
        (conversion) => conversion.purpose === "usage" && Number(conversion.is_active) === 1
      );

      usageConversions.forEach((conversion) => {
        [conversion.from_unit, conversion.to_unit].forEach((unit) => {
          if (!unit) return;
          const unitId = Number(unit.id);
          if (!unitId || units.has(unitId)) return;
          units.set(unitId, { id: unitId, label: this.unitLabel(unit) });
        });
      });

      return connectedUnitIds(usageConversions, baseUnitId, ["usage"])
        .map((unitId) => units.get(unitId))
        .filter(Boolean);
    },
    addLine() {
      if (!this.canAddLine) return;
      const ingredient = this.draftIngredient;
      if (!ingredient) return;
      const unit = this.draftUnitOptions.find((option) => option.id === this.draft.unit_id);
      const baseUnitId = Number(ingredient.unit_id);
      const factor =
        Number(this.draft.unit_id) === baseUnitId
          ? 1
          : conversionFactor(
              (ingredient.unit_conversions || []).filter((row) => row.purpose === "usage"),
              Number(this.draft.unit_id),
              baseUnitId,
              ["usage"]
            );

      this.form.ingredients.push({
        ingredient_id: ingredient.id,
        name_la: ingredient.name_la,
        name_en: ingredient.name_en,
        name_th: ingredient.name_th,
        name_cn: ingredient.name_cn,
        quantity: Number(this.draft.quantity),
        unit_id: this.draft.unit_id,
        unit_name: unit?.label || "",
        cost: Number(this.draft.quantity) * (factor || 0) * Number(ingredient.unit_cost || 0),
      });

      this.draft = { ingredient_id: null, quantity: null, unit_id: null };
    },
    removeLine(index) {
      this.form.ingredients.splice(index, 1);
    },
    ingredientName(line) {
      const locale = this.$i18n.locale;
      if (locale === "en" && line.name_en) return line.name_en;
      if (locale === "th" && line.name_th) return line.name_th;
      if ((locale === "cn" || locale === "ch") && line.name_cn) return line.name_cn;
      return line.name_la || line.name_en || line.name_th || line.name_cn || "-";
    },
    async submitForm() {
      this.submitted = true;
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;
      if (this.linkIngredient && !this.form.ingredients.length) return;

      try {
        this.saving = true;
        const body = this.buildPayload();
        const config = { headers: { "Content-Type": "multipart/form-data" } };
        const { data } = this.isEdit
          ? await this.$http.post(`toppings_update/${this.toppingId}`, body, config)
          : await this.$http.post("toppings", body, config);
        if (data.status) {
          await success(this, data.message);
          this.goBack();
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.saving = false;
      }
    },
    buildPayload() {
      const formData = new FormData();
      formData.append("name_la", this.form.name_la);
      if (this.form.name_en) formData.append("name_en", this.form.name_en);
      if (this.form.name_th) formData.append("name_th", this.form.name_th);
      if (this.form.name_cn) formData.append("name_cn", this.form.name_cn);
      formData.append("price", this.form.price);
      formData.append("is_active", this.form.is_active ? 1 : 0);
      if (this.form.currency_id) {
        formData.append("currency_id", this.form.currency_id);
      }

      const lines = this.linkIngredient ? this.form.ingredients : [];
      lines.forEach((line, index) => {
        formData.append(`ingredients[${index}][ingredient_id]`, line.ingredient_id);
        formData.append(`ingredients[${index}][quantity]`, line.quantity);
        formData.append(`ingredients[${index}][unit_id]`, line.unit_id);
      });

      if (this.imgFile instanceof File) {
        formData.append("img", this.imgFile);
      }

      return formData;
    },
    mapToppingToForm(item = {}) {
      return {
        id: item.id || null,
        name_la: item.name_la || "",
        name_en: item.name_en || "",
        name_th: item.name_th || "",
        name_cn: item.name_cn || "",
        img: item.img || null,
        ingredients: (item.ingredients || []).map((line) => ({
          ingredient_id: line.ingredient_id,
          name_la: line.name_la,
          name_en: line.name_en,
          name_th: line.name_th,
          name_cn: line.name_cn,
          quantity: Number(line.quantity),
          unit_id: line.unit_id,
          unit_name: line.unit_name,
          cost: Number(line.cost),
        })),
        price: item.price || 0,
        currency_id: item.currency_id || null,
        is_active: Number(item.is_active) === 1,
      };
    },
    unitLabel(unit) {
      if (!unit) return "-";
      const localizedField = { en: "name_en", th: "name_th", ch: "name_cn" }[
        this.$i18n.locale
      ];
      const name = (localizedField && unit[localizedField]) || unit.name;
      return name || "-";
    },
    requiredErrors(field) {
      const errors = [];
      if (!this.$v.form[field].$dirty) return errors;
      if (!this.$v.form[field].required) {
        errors.push(this.$t("field_required"));
      }
      return errors;
    },
    numberText(value) {
      const number = value === null || value === undefined || value === "" ? 0 : Number(value);
      return number.toLocaleString();
    },
    goBack() {
      this.$router.push("/manage-topping");
    },
  },
};
</script>

<style scoped>
.topping-form-page {
  padding-bottom: 40px;
}

/* Header & Back Button */
.ap-header-row {
  margin-bottom: 24px;
}

.ap-back-btn {
  min-width: 42px !important;
  width: 42px;
  height: 42px !important;
  padding: 0 !important;
  border: 1px solid #e2e8f0 !important;
  background: #ffffff !important;
  color: var(--app-primary, #5b39c9) !important;
  transition: all 0.15s ease;
}

.ap-back-btn:hover {
  background: rgba(91, 57, 201, 0.06) !important;
  border-color: var(--app-primary, #5b39c9) !important;
}

.theme--dark .ap-back-btn {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.topping-form-title {
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
}

.theme--dark .topping-form-title {
  color: #ffffff;
}

/* PosSmart Card & Section Styles */
.ap-card {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0 !important;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
  overflow: hidden;
}

.theme--dark .ap-card {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.ap-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  background: #ffffff;
}

.theme--dark .ap-card-title {
  background: #1e1e2d !important;
  color: #ffffff !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.ap-card-accent {
  width: 3px;
  height: 15px;
  border-radius: 2px;
  background: var(--app-primary, #5b39c9);
  display: inline-block;
}

.section-label {
  font-size: 13.5px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}

.theme--dark .section-label {
  color: rgba(255, 255, 255, 0.88);
}

.section-block {
  margin-top: 22px;
}

.required-mark {
  color: #ef4444;
  font-weight: 700;
}

.upload-hint {
  color: #94a3b8;
  font-size: 12px;
}

/* Form Inputs Grid */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}

/* Input Fields Appearance */
.ap-card ::v-deep .v-text-field--outlined .v-input__slot {
  background: #f8fafc !important;
  border-radius: 12px !important;
  min-height: 44px;
  border: 1px solid #e2e8f0 !important;
}

.ap-card ::v-deep .v-text-field--outlined fieldset {
  border: none !important;
}

.ap-card ::v-deep .v-text-field--outlined.v-input--is-focused .v-input__slot {
  background: #ffffff !important;
  border-color: var(--app-primary, #5b39c9) !important;
  box-shadow: 0 0 0 3px rgba(91, 57, 201, 0.12) !important;
}

.theme--dark .ap-card ::v-deep .v-text-field--outlined .v-input__slot {
  background: #262638 !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .ap-card ::v-deep .v-text-field--outlined.v-input--is-focused .v-input__slot {
  border-color: #8b6ff0 !important;
  box-shadow: 0 0 0 3px rgba(139, 111, 240, 0.2) !important;
}

/* Language Switcher Row */
.language-toggle-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Ingredient Box */
.participant-box {
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #f8fafc;
}

.theme--dark .participant-box {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);
}

.ingredient-line-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ingredient-line-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.15s ease;
}

.ingredient-line-row:hover {
  border-color: #cbd5e1;
}

.theme--dark .ingredient-line-row {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.08);
}

.ingredient-line-name {
  font-size: 13.5px;
  color: #0f172a;
}

.theme--dark .ingredient-line-name {
  color: #ffffff;
}

.ingredient-line-meta {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.theme--dark .ingredient-line-meta {
  color: #94a3b8;
}

/* Cost Preview Summary Bar */
.cost-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: rgba(91, 57, 201, 0.06);
  border: 1px solid rgba(91, 57, 201, 0.12);
  border-radius: 12px;
}

.theme--dark .cost-preview {
  background: rgba(139, 111, 240, 0.1);
  border-color: rgba(139, 111, 240, 0.2);
}

.cost-preview-label {
  font-size: 13px;
  color: var(--app-primary, #5b39c9);
}

.theme--dark .cost-preview-label {
  color: #8b6ff0;
}

.cost-preview-value {
  font-size: 18px;
  color: var(--app-primary, #5b39c9);
  letter-spacing: -0.01em;
}

.theme--dark .cost-preview-value {
  color: #8b6ff0;
}

.line-error {
  color: #ef4444;
  font-size: 12px;
}

/* Sidebar Live Topping Card Preview */
.ps-topping-preview-card {
  position: relative;
  padding: 18px;
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1.5px dashed #cbd5e1;
  overflow: hidden;
  transition: all 0.2s ease;
}

.theme--dark .ps-topping-preview-card {
  background: linear-gradient(135deg, #262638 0%, #1e1e2d 100%);
  border-color: rgba(255, 255, 255, 0.16);
}

.ps-topping-preview-card--inactive {
  opacity: 0.65;
  filter: grayscale(0.5);
}

.ps-topping-preview-brand {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.05em;
  color: var(--app-primary, #5b39c9);
}

.ps-topping-preview-avatar {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(91, 57, 201, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.theme--dark .ps-topping-preview-avatar {
  background: rgba(139, 111, 240, 0.16);
}

.ps-topping-preview-title {
  font-size: 15px;
  color: #0f172a;
  line-height: 1.25;
}

.theme--dark .ps-topping-preview-title {
  color: #ffffff;
}

.ps-topping-preview-sub {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.ps-topping-preview-divider {
  border-top: 1px dashed #e2e8f0;
}

.theme--dark .ps-topping-preview-divider {
  border-top-color: rgba(255, 255, 255, 0.12);
}

.ps-topping-preview-footer {
  border-top: 1px solid #f1f5f9;
}

.theme--dark .ps-topping-preview-footer {
  border-top-color: rgba(255, 255, 255, 0.08);
}

/* Enable Switch Row */
.enable-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.bg-slate-50 {
  background: #f8fafc;
}

.theme--dark .bg-slate-50 {
  background: rgba(255, 255, 255, 0.04) !important;
}

/* Bottom Action Bar */
.topping-form-actions-bar {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0 !important;
  background: #ffffff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04) !important;
}

.theme--dark .topping-form-actions-bar {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.form-action-btn-primary {
  height: 44px !important;
  font-size: 14px;
  font-weight: 700;
}

/* Skeleton Loading */
.skeleton-block {
  border-radius: 8px;
  background: linear-gradient(90deg, #eef1f5 25%, #f7f9fb 37%, #eef1f5 63%);
  background-size: 400% 100%;
  animation: topping-skeleton-shimmer 1.4s ease infinite;
}

@keyframes topping-skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

.skeleton-label {
  width: 120px;
  height: 14px;
}

.skeleton-upload-area {
  width: 100%;
  height: 200px;
  border-radius: 14px;
}

.skeleton-line {
  width: 60%;
  height: 16px;
}

.skeleton-field {
  width: 100%;
  height: 44px;
  border-radius: 10px;
}

.theme--dark .skeleton-block {
  background: linear-gradient(90deg, #262626 25%, #333 37%, #262626 63%);
  background-size: 400% 100%;
}
</style>
